<template>
  <div class="fair-survey-step fair-survey-step--swipe">
    <div class="fair-survey-step__head fair-survey-step__head--centered">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <p
      v-if="isLoadingBatch && cards.length === 0"
      class="fair-survey-step__note"
    >
      Загрузка образов…
    </p>

    <FlashCards
      v-else-if="cards.length > 0"
      class="tinder-like-step__deck"
      :items="cards"
      @approve="onApprove"
      @reject="onReject"
    >
      <template #default="{ item }">
        <TinderLikeCard :item="item" />
      </template>

      <template #actions="{ approve, reject }">
        <div class="tinder-like-step__actions">
          <button
            type="button"
            class="tinder-like-step__action"
            aria-label="Не нравится"
            :disabled="isSubmittingAction"
            @click="reject"
          >
            <FairIcon
              name="close"
              :size="20"
            />
          </button>
          <button
            type="button"
            class="tinder-like-step__action tinder-like-step__action--like"
            aria-label="Нравится"
            :disabled="isSubmittingAction"
            @click="approve"
          >
            <FairIcon
              name="favorite-filled"
              :size="20"
            />
          </button>
        </div>
      </template>
    </FlashCards>

    <div
      v-else-if="error"
      class="tinder-like-step__state"
    >
      <p class="fair-survey-step__note">
        {{ error }}
      </p>
      <FairButton
        label="Попробовать ещё раз"
        variant="tonal"
        size="m"
        :rounded="false"
        @click="loadNextBatch"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useLookModal } from '@/features/look/store'
import type { LookStep, TinderCardItem } from '@/features/look/types/look-config'
import { fetchNextLookCard, submitLookCardAction } from '@/features/look/api/look'

import { FlashCards } from '@/components/HelpersComponents/Cards'
import TinderLikeCard from '@/components/HelpersComponents/Cards/components/TinderLikeCard.vue'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'

interface Props {
  step: LookStep & { type: 'tinder_like' }
}

const props = defineProps<Props>()
const lookStore = useLookModal()

// Константы
const BATCH_SIZE = 5
const MIN_CARDS_BEFORE_LOAD = 2

// Состояние
const cards = ref<Array<{ id: number; text: string; description: string; image: string; originalId?: string }>>([])
const processedCardsCount = ref(0)
const loadedCardsCount = ref(0)
const isLoadingBatch = ref(false)
const isSubmittingAction = ref(false)
const error = ref<string | null>(null)

const cardsCount = computed(() => props.step.cardsCount)
const lookConfigId = computed(() => lookStore.lookModal.config?.id || '')

function adaptCard(card: TinderCardItem): { id: number; text: string; description: string; image: string; originalId: string } {
  const numericId = parseInt(card.id, 10) || 0
  
  return {
    id: numericId,
    text: card.text,
    description: card.subtitle,
    image: card.image,
    originalId: card.id
  }
}

async function loadNextBatch() {
  if (!lookConfigId.value) {
    error.value = 'Конфигурация опроса не загружена'
    return
  }

  if (loadedCardsCount.value >= cardsCount.value) {
    return
  }

  const remainingCards = cardsCount.value - loadedCardsCount.value
  const batchToLoad = Math.min(BATCH_SIZE, remainingCards)

  isLoadingBatch.value = true
  error.value = null

  try {
    const newCards = await fetchNextLookCard({
      lookConfigId: lookConfigId.value,
      currentCardIndex: loadedCardsCount.value,
      limit: batchToLoad
    })

    for (const card of newCards) {
      if (!card.id) {
        throw new Error('Карточка не содержит обязательный id')
      }
    }

    const adaptedNewCards = newCards.map(adaptCard)
    cards.value.push(...adaptedNewCards)
    loadedCardsCount.value += newCards.length

    const stepKey = `${props.step.id}_cardIndex`
    lookStore.updateLookData(stepKey, processedCardsCount.value)
  } catch (err) {
    console.error('Error loading batch:', err)
    error.value = err instanceof Error ? err.message : 'Не удалось загрузить образы'
  } finally {
    isLoadingBatch.value = false
  }
}

function checkAndLoadNextBatch() {
  const remainingInArray = cards.value.length - processedCardsCount.value
  
  if (remainingInArray <= MIN_CARDS_BEFORE_LOAD && loadedCardsCount.value < cardsCount.value) {
    loadNextBatch()
  }
}

async function checkCompletion() {
  await nextTick()
  
  if (processedCardsCount.value >= cardsCount.value) {
    if (props.step.autoNext) {
      lookStore.nextStep()
    }
  }
}

async function handleCardAction(card: { id: number; text?: string; description?: string; image?: string; originalId?: string }, action: 'like' | 'dislike') {
  if (!lookConfigId.value) {
    console.error('[TinderLikeStep] Config id is missing')
    return
  }

  const cardId = card.originalId || card.id.toString()

  isSubmittingAction.value = true
  error.value = null

  try {
    await submitLookCardAction({
      lookConfigId: lookConfigId.value,
      cardId,
      action
    })

    processedCardsCount.value++
    
    const stepKey = `${props.step.id}_cardIndex`
    lookStore.updateLookData(stepKey, processedCardsCount.value)

    checkAndLoadNextBatch()
    await checkCompletion()
  } catch (err) {
    console.error('[TinderLikeStep] Error submitting card action:', err)
    const isMockMode = lookConfigId.value.includes('mock')
    
    if (isMockMode) {
      processedCardsCount.value++
      const stepKey = `${props.step.id}_cardIndex`
      lookStore.updateLookData(stepKey, processedCardsCount.value)
      checkAndLoadNextBatch()
      await checkCompletion()
    } else {
      error.value = err instanceof Error ? err.message : 'Не удалось отправить действие. Попробуйте еще раз.'
    }
  } finally {
    isSubmittingAction.value = false
  }
}

function onApprove(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onApprove')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'like')
}

function onReject(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onReject')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'dislike')
}

function initializeStep() {
  const stepKey = `${props.step.id}_cardIndex`
  const savedIndex = lookStore.lookModal.lookData[stepKey]
  
  if (typeof savedIndex === 'number' && savedIndex >= 0) {
    processedCardsCount.value = savedIndex
    loadedCardsCount.value = Math.ceil(savedIndex / BATCH_SIZE) * BATCH_SIZE
  } else {
    processedCardsCount.value = 0
    loadedCardsCount.value = 0
  }
  
  cards.value = []
  error.value = null
  isLoadingBatch.value = false
  isSubmittingAction.value = false
  
  loadNextBatch()
}

watch(processedCardsCount, async (newCount) => {
  const stepKey = `${props.step.id}_cardIndex`
  lookStore.updateLookData(stepKey, newCount)
  
  await nextTick()
  if (newCount >= cardsCount.value && props.step.autoNext) {
    lookStore.nextStep()
  }
})

onMounted(() => {
  initializeStep()
})

watch(() => props.step.id, () => {
  initializeStep()
}, { immediate: false })
</script>
<style scoped>
.fair-survey-step--swipe {
  height: 100%;
  align-items: center;
  gap: var(--fair-rd-space-16);
}

.tinder-like-step__deck {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

.tinder-like-step__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--fair-rd-space-16);
  padding-top: var(--fair-rd-space-16);
}

/* Both verdicts get the same weight — the icon carries the meaning. */
.tinder-like-step__action {
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 1px solid var(--fair-rd-line-hairline);
  border-radius: var(--fair-rd-radius-full);
  background: var(--fair-rd-bg-surface);
  color: var(--fair-rd-text-secondary);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.tinder-like-step__action:hover:not(:disabled) {
  border-color: var(--fair-rd-border-control);
  color: var(--fair-rd-text-primary);
}

.tinder-like-step__action--like:hover:not(:disabled) {
  color: var(--fair-rd-signal-positive-text);
}

.tinder-like-step__action:disabled {
  color: var(--fair-rd-disabled);
  cursor: default;
}

.tinder-like-step__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--fair-rd-space-16);
  margin: auto 0;
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .tinder-like-step__action {
    transition: none;
  }
}
</style>
